// A generic month-style calendar grid (Mon-first, 7-col weeks) used to
// visualize flows in the admin app:
//   • PatientFlows — active flows laid out on their concrete due dates.
//   • FlowEditor   — a flow's steps laid out on their relative day offsets.
// Purely presentational: the caller builds the week matrix and supplies chips.
// Colors are inline styles (not Tailwind classes) on purpose — variable color
// values would otherwise be purged from the production CSS.

export type CalendarChip = {
  id: string;
  label: string;
  /** Optional "HH:MM" rendered before the label. */
  time?: string | null;
  /** Hex color for the chip's dot/tint. */
  color: string;
  /** Optional secondary text shown muted (e.g. source flow name). */
  meta?: string;
};

export type CalendarCell = {
  /** React key + identity for the cell. */
  key: string;
  /** Top-left label, e.g. a day-of-month number or "Dag 3". */
  label: string;
  /** Dim the cell (outside the focused month / padding). */
  muted?: boolean;
  /** Emphasize the cell (today / day 0). */
  highlight?: boolean;
  chips: CalendarChip[];
  /** Click on the cell body (not a chip) — used to add a step. */
  onAdd?: () => void;
};

// A deterministic, brand-adjacent palette. Assign by stable index so a given
// flow keeps the same color across renders.
// 12 distinct hues: covers all 9 FLOW_MODULE_TYPES without a wrap-collision and
// keeps a patient's active flows distinguishable well past the common count.
export const CALENDAR_PALETTE = [
  "#6b8af0",
  "#3fb6a0",
  "#e0a455",
  "#d175a6",
  "#8a7cd9",
  "#4fa9cf",
  "#d97f6e",
  "#86a83f",
  "#c06bd1",
  "#e0735a",
  "#4cae8a",
  "#b59a3c",
] as const;

export function paletteColor(index: number): string {
  return CALENDAR_PALETTE[
    ((index % CALENDAR_PALETTE.length) + CALENDAR_PALETTE.length) %
      CALENDAR_PALETTE.length
  ];
}

// --- date helpers ----------------------------------------------------------

/** Parse a "YYYY-MM-DD" string as a *local* date (avoids UTC-shift off-by-one). */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** "YYYY-MM-DD" for a local Date. */
export function toISODate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/**
 * Build a Mon-first matrix of weeks covering the given month. Each entry is a
 * local Date and whether it falls inside `month`. Trailing all-padding weeks
 * are trimmed; the grid is always 4–6 weeks.
 */
export function buildMonthMatrix(
  year: number,
  month: number,
): { date: Date; inMonth: boolean }[][] {
  const first = new Date(year, month, 1);
  // Mon=0 … Sun=6
  const lead = (first.getDay() + 6) % 7;
  const start = new Date(year, month, 1 - lead);
  const weeks: { date: Date; inMonth: boolean }[][] = [];
  const cursor = new Date(start);
  for (let w = 0; w < 6; w++) {
    const week: { date: Date; inMonth: boolean }[] = [];
    for (let d = 0; d < 7; d++) {
      week.push({
        date: new Date(cursor),
        inMonth: cursor.getMonth() === month,
      });
      cursor.setDate(cursor.getDate() + 1);
    }
    // Only keep weeks that touch the month; a leading/trailing all-padding week
    // (e.g. months ending on a Sunday) is dropped so the grid is 4–6 weeks.
    if (week.some((c) => c.inMonth)) weeks.push(week);
  }
  return weeks;
}

const WEEKDAY_LABELS = ["Mån", "Tis", "Ons", "Tor", "Fre", "Lör", "Sön"];

// --- component -------------------------------------------------------------

export function FlowCalendarGrid({
  weeks,
  showWeekdays = false,
  onChipClick,
}: {
  weeks: CalendarCell[][];
  /** Show the Mon–Sun header row (real-date calendars). */
  showWeekdays?: boolean;
  onChipClick?: (chip: CalendarChip) => void;
}) {
  return (
    <div className="overflow-hidden rounded-md border border-surfaceMuted">
      {showWeekdays ? (
        <div className="grid grid-cols-7 bg-surfaceMuted/40">
          {WEEKDAY_LABELS.map((w) => (
            <div
              key={w}
              className="px-xs py-xs text-[10px] font-semibold uppercase tracking-wide text-textSecondary text-center"
            >
              {w}
            </div>
          ))}
        </div>
      ) : null}
      <div className="flex flex-col">
        {weeks.map((week, wi) => (
          <div key={wi} className="grid grid-cols-7">
            {week.map((cell) => (
              <div
                key={cell.key}
                onClick={cell.onAdd}
                className={[
                  "group relative min-h-[84px] border-b border-r border-surfaceMuted p-xs flex flex-col gap-[2px] last:border-r-0",
                  cell.muted ? "bg-surfaceMuted/20" : "bg-surface",
                  cell.onAdd ? "cursor-pointer hover:bg-brand/5" : "",
                ].join(" ")}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={[
                      "text-[11px] tabular-nums",
                      cell.highlight
                        ? "font-semibold text-brand"
                        : cell.muted
                          ? "text-textSecondary/60"
                          : "text-textSecondary",
                    ].join(" ")}
                  >
                    {cell.label}
                  </span>
                  {cell.onAdd ? (
                    <span className="text-textSecondary/0 group-hover:text-brand text-sm leading-none">
                      +
                    </span>
                  ) : null}
                </div>
                {cell.chips.map((chip) => (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onChipClick?.(chip);
                    }}
                    title={`${chip.time ? chip.time + " " : ""}${chip.label}${chip.meta ? " — " + chip.meta : ""}`}
                    className="flex items-center gap-[4px] rounded-sm px-[4px] py-[2px] text-left text-[10px] leading-tight hover:opacity-80"
                    style={{ backgroundColor: `${chip.color}1f` }}
                  >
                    <span
                      className="h-[6px] w-[6px] shrink-0 rounded-full"
                      style={{ backgroundColor: chip.color }}
                    />
                    {chip.time ? (
                      <span className="shrink-0 tabular-nums text-textSecondary">
                        {chip.time}
                      </span>
                    ) : null}
                    <span className="truncate text-textPrimary">
                      {chip.label}
                    </span>
                  </button>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
